import { memo, useMemo } from "react";
import { useNavigate } from "react-router-dom";
import { formatCurrency, transformRoute } from "helpers";
import { PriceCompareItem } from "services/api/dashboard/types";
import { Routes as FleetVehicleRoutes } from "ui/modules/fleet/vehicles/navigation";
import DashboardTooltip, { TooltipDivider, TooltipRow, TooltipTitle } from "../../../../common/DashboardTooltip";
import CardHeader, { DeltaBadge } from "../../../../common/CardHeader";
import { text } from "../../../../common/dashboardPalette";

interface VehiclePriceCompareCardProps {
  data: PriceCompareItem[];
}

/** Nabavna vs prodajna cijena — horizontalni layout per-vozilo. */
const VehiclePriceCompareCard: React.FC<VehiclePriceCompareCardProps> = ({ data: rawData }) => {
  const navigate = useNavigate();
  const data = Array.isArray(rawData) ? rawData : [];
  const maxVal = useMemo(
    () => Math.max(...data.map((d) => Math.max(d.sellingPrice, d.totalPurchasePrice)), 1),
    [data]
  );

  const totalPurchase = useMemo(() => data.reduce((s, d) => s + d.totalPurchasePrice, 0), [data]);
  const totalSelling = useMemo(() => data.reduce((s, d) => s + d.sellingPrice, 0), [data]);
  const totalMargin = totalSelling - totalPurchase;
  const marginPct = totalPurchase > 0 ? (totalMargin / totalPurchase) * 100 : 0;
  const avgSelling = data.length > 0 ? totalSelling / data.length : 0;
  const avgPurchase = data.length > 0 ? totalPurchase / data.length : 0;

  if (data.length === 0) {
    return (
      <div className="rounded-sm px-4 py-5 bg-white h-full flex items-center justify-center">
        <span className="text-sm text-slate-400">Nema podataka</span>
      </div>
    );
  }

  return (
    <div className="rounded-sm px-4 py-5 bg-white">
      <CardHeader
        title="Nabavna vs prodajna"
        subtitle="Zadnjih 10 prodanih vozila"
        className="mb-6"
        right={
          <DashboardTooltip
            trigger={
              <div className="flex flex-col items-end cursor-help gap-1">
                <span className={`text-2xl font-bold ${text.primary}`}>
                  {formatCurrency.format(totalMargin)}
                </span>
                <DeltaBadge value={marginPct} />
              </div>
            }
          >
            <TooltipTitle>Pregled cijena</TooltipTitle>
            <TooltipRow label="Nabavna" value={formatCurrency.format(totalPurchase)} />
            <TooltipRow label="Prodajna" value={formatCurrency.format(totalSelling)} />
            <TooltipDivider />
            <TooltipRow label="⌀ Nabavna" value={formatCurrency.format(avgPurchase)} emphasis="muted" />
            <TooltipRow label="⌀ Prodajna" value={formatCurrency.format(avgSelling)} emphasis="muted" />
            <TooltipDivider />
            <TooltipRow
              label="Marža"
              value={`${formatCurrency.format(totalMargin)} (${marginPct.toFixed(1)}%)`}
              emphasis={totalMargin >= 0 ? "positive" : "negative"}
            />
          </DashboardTooltip>
        }
      />

      <div className="space-y-2">
        {data.map((item, i) => {
          const itemMargin = item.sellingPrice - item.totalPurchasePrice;
          const itemPct = item.totalPurchasePrice > 0 ? (itemMargin / item.totalPurchasePrice) * 100 : 0;
          const purchaseWidth = (item.totalPurchasePrice / maxVal) * 100;
          const sellingWidth = (item.sellingPrice / maxVal) * 100;
          const isCurrent = i === 0;

          return (
            <div
              key={item.id}
              className="group/row relative rounded-sm ring-1 ring-slate-200 bg-white px-3 py-2.5 cursor-pointer transition-all hover:ring-2 hover:ring-emerald-400 hover:shadow-sm"
              onClick={() =>
                navigate(transformRoute(FleetVehicleRoutes.FleetVehicleDetailPageRouter, { id: item.id }))
              }
            >
              <div className="flex items-center justify-between gap-2 mb-2">
                <span className={`text-sm font-semibold truncate ${isCurrent ? "text-emerald-700" : "text-slate-800"}`}>
                  {item.name}
                </span>
                <span
                  className={`text-xs font-medium px-1.5 py-0.5 rounded flex-shrink-0 ${
                    itemMargin >= 0 ? "bg-emerald-50 text-emerald-700" : "bg-rose-50 text-rose-700"
                  }`}
                >
                  {itemMargin >= 0 ? "+" : ""}{formatCurrency.format(itemMargin)} ({itemPct.toFixed(0)}%)
                </span>
              </div>

              <div className="space-y-1.5">
                <div className="flex items-center gap-2">
                  <span className="text-xs text-slate-400 w-16 flex-shrink-0">Nabavna</span>
                  <div className="flex-1 h-3 bg-slate-100 rounded-sm overflow-hidden">
                    <div
                      className="h-full bg-gradient-to-l from-slate-400/70 to-slate-400/20 border-r-2 border-slate-400 origin-left animate-growRight transition-all duration-500 group-hover/row:from-slate-400/85"
                      style={{ width: `${Math.max(purchaseWidth, 2)}%` }}
                    />
                  </div>
                  <span className="text-xs text-slate-500 w-20 text-right flex-shrink-0">
                    {formatCurrency.format(item.totalPurchasePrice)}
                  </span>
                </div>
                <div className="flex items-center gap-2">
                  <span className="text-xs text-slate-400 w-16 flex-shrink-0">Prodajna</span>
                  <div className="flex-1 h-3 bg-slate-100 rounded-sm overflow-hidden">
                    <div
                      className="h-full bg-gradient-to-l from-emerald-500/70 to-emerald-500/20 border-r-2 border-emerald-500 origin-left animate-growRight transition-all duration-500 group-hover/row:from-emerald-500/85"
                      style={{ width: `${Math.max(sellingWidth, 2)}%` }}
                    />
                  </div>
                  <span className="text-xs text-slate-700 font-medium w-20 text-right flex-shrink-0">
                    {formatCurrency.format(item.sellingPrice)}
                  </span>
                </div>
              </div>
            </div>
          );
        })}
      </div>

      <div className="flex items-center justify-center gap-4 mt-4">
        <div className="flex items-center gap-1.5">
          <span className="w-2.5 h-2.5 rounded-sm bg-slate-300" />
          <span className={`text-sm ${text.muted}`}>Nabavna</span>
        </div>
        <div className="flex items-center gap-1.5">
          <span className="w-2.5 h-2.5 rounded-sm bg-emerald-500" />
          <span className={`text-sm ${text.muted}`}>Prodajna</span>
        </div>
      </div>
    </div>
  );
};

export default memo(VehiclePriceCompareCard);
